import React, { useEffect } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useGoogleMapsPlaces } from "../GoogleMaps/GoogleMapsContext";

export const EMPTY_CITY_LOCATION = {
  address: "",
  city: "",
  state: "",
  country: "",
  zip: "",
  latitude: "",
  longitude: "",
  placeId: "",
};

export const getResolvedCity = (location = EMPTY_CITY_LOCATION) =>
  location?.city?.trim() ||
  location?.address?.split(",")[0]?.trim() ||
  "";

export const hasValidCoordinates = (location = EMPTY_CITY_LOCATION) =>
  location?.latitude !== "" &&
  location?.latitude != null &&
  location?.longitude !== "" &&
  location?.longitude != null;

export const parseCityFromGeocodeResult = (result, description = "") => {
  let city = "";
  let state = "";
  let country = "";
  let zip = "";

  result?.address_components?.forEach((comp) => {
    if (comp.types.includes("locality")) city = comp.long_name;
    if (comp.types.includes("administrative_area_level_1")) {
      state = comp.short_name || comp.long_name;
    }
    if (comp.types.includes("country")) country = comp.long_name;
    if (comp.types.includes("postal_code")) zip = comp.long_name;
  });

  if (!city) {
    const fallbackTypes = [
      "sublocality",
      "sublocality_level_1",
      "administrative_area_level_3",
      "administrative_area_level_2",
      "neighborhood",
      "postal_town",
    ];

    for (const comp of result?.address_components || []) {
      if (fallbackTypes.some((type) => comp.types.includes(type))) {
        city = comp.long_name;
        break;
      }
    }
  }

  if (!city && description) {
    city = description.split(",")[0].trim();
  }

  return { city, state, country, zip };
};

const CityLocationSearch = ({
  value = EMPTY_CITY_LOCATION,
  onChange,
  disabled = false,
  label = "Service Area",
  placeholder = "Search city",
}) => {
  const { placesEnabled } = useGoogleMapsPlaces();

  const {
    ready,
    value: placeValue,
    suggestions: { status, data },
    setValue: setPlaceValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ["(cities)"],
      componentRestrictions: { country: "us" },
    },
    debounce: 300,
    initOnMount: placesEnabled,
  });

  const inputReady = placesEnabled && ready;

  useEffect(() => {
    if (value?.address) {
      setPlaceValue(value.address, false);
    }
  }, [value?.address, setPlaceValue]);

  const handleSelectPlace = async (description) => {
    setPlaceValue(description, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: description });
      if (!results?.length) return;

      const { lat, lng } = await getLatLng(results[0]);
      const { city, state, country, zip } = parseCityFromGeocodeResult(
        results[0],
        description,
      );

      onChange?.({
        address: description,
        city,
        state,
        country,
        zip,
        latitude: lat,
        longitude: lng,
        placeId: results[0].place_id,
      });
    } catch (error) {
      console.error("City location lookup failed", error);
    }
  };

  return (
    <div className="company-form__field">
      <label>
        {label} <span style={{ color: "#cc4645" }}>*</span>
      </label>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          placeholder={
            inputReady ? placeholder : "Loading location search…"
          }
          value={placeValue}
          onChange={(e) => {
            setPlaceValue(e.target.value);
            if (!e.target.value.trim()) {
              onChange?.({ ...EMPTY_CITY_LOCATION });
            }
          }}
          disabled={disabled || !inputReady}
          autoComplete="off"
        />
        {inputReady && status === "OK" && data.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 30,
              background: "#fff",
              border: "1px solid #ccc",
              width: "100%",
              maxHeight: 200,
              overflowY: "auto",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            }}
          >
            {data.map(({ place_id, description }) => (
              <div
                key={place_id}
                style={{ padding: 8, cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectPlace(description)}
              >
                {description}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CityLocationSearch;
